import { Component, ViewChild, Input, OnInit, OnDestroy, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { Subject, takeUntil, lastValueFrom, firstValueFrom, skip } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { clearStore } from 'src/app/core/store/form.actions';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { MyPurchaseForm } from 'src/app/shared/models/app-models/MyPurchaseForm.model';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MyDocuments } from 'src/app/shared/models/database-models';
import { ExtendedKeycloakUser, StoreDropdowns, UserInformation } from 'src/app/shared/models/app-models';
import { MyDocumentsData } from 'src/app/shared/models/database-models/MyDocuments.model';
import { clearTaggingStore } from 'src/app/core/store/TaggedVendors.action';
import { clearDeletedItemVendorStore } from 'src/app/core/store/DeletedItemVendors.action';
import { clearAttachmentStore } from 'src/app/core/store/attachment.action';
import { pageName } from 'src/app/shared/services/page.service';
import {Clipboard} from '@angular/cdk/clipboard';
import { KeycloakService } from 'keycloak-angular';

@Component({
  selector: 'app-my-documents-listing',
  templateUrl: './my-documents-listing.component.html',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  styleUrls: ['./my-documents-listing.component.scss']
})
export class MyDocumentsListingComponent implements OnInit, OnDestroy {
  @ViewChild(MatTable) table: MatTable<any> | undefined;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @Input() docStatus: string = '';
  @Input() index!: number;
  @Input() selectedIndex!: number;
  @Input() uponCompletingDocumentLoadCallbackFunction: (args: number) => void = () => { };
  @Input() filterByDocumentNumber: string = ''
  @Input() search: boolean = false
  @Input() initialData: MyDocuments[] | null = null;
  @Input() badgeCount:number=0;
  myDocsData: MyDocuments[] = [];
  columnsToDisplay = ['DocumentNo', 'PurposeOfPurchase', 'DocumentStatus', 'WorkflowStatus', 'CreatedOn', 'RequestingOfficer', 'LastModified', 'PurchaseMode'];
  columnsToDisplayWithExpand = [...this.columnsToDisplay];
  expandedElement!: MyDocuments | null;
  destroy$: Subject<boolean> = new Subject<boolean>();
  pageSize = 20;
  pageSizeOptions: number[] = [20, 40, 60];
  currentPage = 0;
  totalCount: number = 0;
  dataSource = new MatTableDataSource<MyDocuments>(this.myDocsData);
  isLoadingData: boolean = true
  loggedInUser!: UserInformation
  usernames: ExtendedKeycloakUser[] = [];
  emptyMessage: string = '';
  purchaseType: string = 'rfq';
  sortSettingMap: any = {};
  currentSort: string | null = null;
  constructor(private backendService: BackendApiService,
    private store: Store<{ form: MyPurchaseForm, user: UserInformation, dp: StoreDropdowns }>, private router: Router,
    private cdr: ChangeDetectorRef,
    private keycloakService: KeycloakService,
    private clipboard: Clipboard) {
    // set current user
    this.store.select('user').pipe(takeUntil(this.destroy$)).subscribe((user: UserInformation) => {
      this.loggedInUser = user;
    })
    // get UserList out from store
    this.store.select('dp').pipe(takeUntil(this.destroy$)).subscribe((dp: StoreDropdowns) => {
      this.usernames = dp.UserList as ExtendedKeycloakUser[]
    })

    this.initSort()


  }

  async ngOnInit(): Promise<void> {
    this.store.dispatch(clearStore());
    this.store.dispatch(clearTaggingStore());
    this.store.dispatch(clearDeletedItemVendorStore())
    this.store.dispatch(clearAttachmentStore());
  }
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes["selectedIndex"] && changes["selectedIndex"].currentValue == this.index) {
      try {
        // ensure that user is not null due to update sequence
        if (this.loggedInUser.userName === '') {
          this.loggedInUser = await firstValueFrom(this.store.select('user').pipe(skip(1)));
        }
        if (this.isLoadingData) {
          if (this.initialData)
            await this.loadData(this.initialData);
          else
            await this.loadData();
          this.isLoadingData = false;
        }
      } catch (e) {
        console.log("Error retrieving my documents")
        this.dataSource.data = [];
         this.isLoadingData = false;
      }
    }
  }
  async loadData(initialData?: MyDocuments[]) {
    if (!initialData) {
      let data: MyDocumentsData;
      if (this.search) data = await this.loadDataForSearch();
      else {
        // Determine deleted visibility based on user roles
        let userRoleFromKeyCloak = this.keycloakService.getUserRoles();
        const isDeletedVisibility = userRoleFromKeyCloak.some(role => ['IT Support', 'Contract Executives', 'Contracts Admin', 'Purchasing Admin', 'Finance Admin', 'Purchase Executives'].includes(role));
        data = await lastValueFrom(
          this.backendService.getMyDocuments(
            (this.currentPage + 1).toString(),
            this.docStatus,
            this.pageSize.toString(),
            this.currentSort,
            this.currentSort ? this.sortSettingMap[this.currentSort] : null,
            isDeletedVisibility
          )
        );
      }
      this.myDocsData = data.documents.map((docs: MyDocuments) => {
        return {
          ...docs, RequestingOfficer: this.usernames.find((user: ExtendedKeycloakUser) => user.id === docs.RequestingOfficer)?.last_name ?? 'unknown',
          RFQSummaryRequestingOfficer: docs.IsRFQSummaryEnable 
          ? this.usernames.find((user: ExtendedKeycloakUser) => user.id === docs.RFQSummaryRequestingOfficer)?.last_name ?? 'unknown'
          : null        
        }
      });
      this.totalCount = data.totalCount;
      if (this.myDocsData.length === 0) this.getEmptyMessage(this.index);
    } else {
      this.myDocsData = initialData.map((docs: MyDocuments) => {
        return {
          ...docs, RequestingOfficer: this.usernames.find((user: ExtendedKeycloakUser) => user.id === docs.RequestingOfficer)?.last_name ?? 'unknown',
          RFQSummaryRequestingOfficer: docs.IsRFQSummaryEnable 
          ? this.usernames.find((user: ExtendedKeycloakUser) => user.id === docs.RFQSummaryRequestingOfficer)?.last_name ?? 'unknown'
          : null        
        }
      })
      this.totalCount = this.badgeCount;
      if (this.totalCount == 0) this.getEmptyMessage(this.index);
    }
    this.dataSource.data = this.myDocsData
    this.uponCompletingDocumentLoadCallbackFunction(this.totalCount)
  }

  async loadDataForSearch() {
    let userRoleFromKeyCloak = this.keycloakService.getUserRoles();
    const isDeletedVisibility = userRoleFromKeyCloak.some(role => ['IT Support', 'Contract Executives', 'Contracts Admin', 'Purchasing Admin', 'Finance Admin', 'Purchase Executives'].includes(role));
    return await lastValueFrom(this.backendService.searchMyDocuments(this.loggedInUser.userName, this.filterByDocumentNumber, (this.currentPage + 1).toString(), this.pageSize.toString(), isDeletedVisibility));
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }



  onPageChange(event: PageEvent) {
    console.log("onPageChange", event);
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadData()
  }

  formatHeader(camelCaseHeader: string): string {
    const words = camelCaseHeader.replace(/([A-Z])/g, ' $1')
      .replace(/^./, function (str) { return str.toUpperCase(); });
    if (words.trim().toLowerCase() === 'document no') {
      return words + '.';
    }
    return words;
  }
  checkIfUserCanAccessDocument(docNo: string) {
    return !!this.myDocsData.find(x => x.DocumentNo == docNo)?.BlockRedirection
  }
  navigateToRFQ(element:MyDocuments, DocumentNo: string, purchaseTypeId: number) {
    sessionStorage.setItem('fromPage', pageName.My_Document);
    if (this.search && this.checkIfUserCanAccessDocument(DocumentNo)) {
      alert('You are not authorised to view this page');
      return;
    }
    let url = ''
    if (element.IsRFQSummaryEnable) {
      url = '/rfqSummary/' + `${DocumentNo}`;
      window.open(url, '_blank');
    }else{
      sessionStorage.setItem('currentTab', '0');
      url = this.getroutepath(DocumentNo, purchaseTypeId);
    }
    if (!this.search) {
      this.router.navigateByUrl(url)
    } else {
      window.localStorage.setItem('fromSearch', this.router.url);
      window.open(url, '_blank');
    }
  }



  getEmptyMessage(tabIndex: number) {
    switch (tabIndex) {
      case 0:
        this.emptyMessage = 'You have no document items.';
        break;
      case 1:
        this.emptyMessage = 'You have no draft document items.';
        break;
      case 2:
        this.emptyMessage = 'You have no pending document items.';
        break;
      case 3:
        this.emptyMessage = 'You have no completed document items.';
        break;
      default:
        this.emptyMessage = 'You have no document items.';
    }

  }

  initSort() {
    for (const column of this.columnsToDisplay) {
      this.sortSettingMap[column] = true;
    }
  }


  resetSort(columnToSort: string) {
    for (const column of this.columnsToDisplay) {
      if (column != columnToSort) {
        this.sortSettingMap[column] = true;
      }
    }
  }

  sortByColumn(column: string) {
    if (this.currentSort != column) {
      this.resetSort(column);
      this.currentSort = column;
    } else {
      this.sortSettingMap[column] = !this.sortSettingMap[column];
    }
    // api call to update order
    this.loadData();
  }

  onContextMenuClick(item: any, action: string) {
    if(action === "open") {
      this.handleNavigate(item["DocumentNo"], item["PurchaseTypeId"], action);
    } else if (action === "tab") {
      this.handleNavigate(item["DocumentNo"], item["PurchaseTypeId"], action);
    } else if (action === "copy-link") {
      this.clipboard.copy(location.origin + this.getroutepath(item["DocumentNo"], item["PurchaseTypeId"]));
    } else if (action === "copy-documentnumber") {
      this.clipboard.copy(item["DocumentNo"]);
    }
  }

  handleNavigate(DocumentNo: string, purchaseTypeId: number, type: string) {
    sessionStorage.setItem('fromPage', pageName.My_Document);
    sessionStorage.setItem('currentTab', '0');
    if (this.search && this.checkIfUserCanAccessDocument(DocumentNo)) {
      alert('You are not authorised to view this page');
      return;
    }
    let url = this.getroutepath(DocumentNo, purchaseTypeId);
    if(type === "tab") {
      window.localStorage.setItem('fromSearch', this.router.url);
      window.open(url, '_blank');
    } else {
      this.router.navigateByUrl(url)
    }
  }

  getroutepath(DocumentNo: string, purchaseTypeId: number): string {
    let path:string = "";

    if(purchaseTypeId) {
      if (purchaseTypeId == 1) path = '/purchase/rfq/';
      if (purchaseTypeId == 6) path = '/purchase/tender/';
      if (purchaseTypeId == 3) path = '/purchase/corporatecard/';
      if (purchaseTypeId == 9) path = '/purchase/permissiblecoveringpurchaseorder/';
      if (purchaseTypeId == 5) path = '/purchase/dc/';
      path += DocumentNo;
    }

    return path; 
  }
}

